import { AutocompleteGenericEntity } from '@australiangreens/ag-internal-components';
import { Box, FormHelperText, Tooltip } from '@mui/material';
import {
  BaseWidget,
  BaseWidgetProps,
  Config,
  FactoryWithContext,
  MultiSelectFieldSettings,
} from '@react-awesome-query-builder/mui';

import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { MappingError } from '@/pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/FilterMappers';
import MissingFilterValueWarning from './MissingFilterValueWarning';
import TemplateCapableFetchAutocomplete from './templateCapableComponents/TemplateCapableFetchAutocomplete';

type FetchAutocompleteValue = {
  value: AutocompleteGenericEntity[] | TemplatePlaceholder;
  errors: MappingError[];
};

type FetchAutocompleteWidgetProps = {
  asyncFetch: (lookupValue: string) => Promise<AutocompleteGenericEntity[]>;
  fetchLabel: string;
  preLoadedOptions?: AutocompleteGenericEntity[];
  isOnTemplatePage?: boolean;
  templateBuilderDisplayName?: string;
};

type CombinedProps = BaseWidgetProps<Config, FetchAutocompleteValue> & FetchAutocompleteWidgetProps;

const FetchAutocompleteFactory: FactoryWithContext<CombinedProps> = ({
  value,
  setValue,
  asyncFetch,
  fetchLabel,
  preLoadedOptions,
  operator,
  isOnTemplatePage,
  templateBuilderDisplayName,
}: CombinedProps) => {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem"
        }}>
        <MissingFilterValueWarning isMissing={(value?.errors.length ?? 0) > 0} />
        <TemplateCapableFetchAutocomplete
          sx={{ minWidth: '70ch', maxWidth: '70ch' }}
          label={fetchLabel}
          key={fetchLabel}
          onChange={(val) =>
            setValue({ value: isTemplatePlaceholder(val) ? val : val.map((x) => x), errors: [] })
          }
          value={value?.value ?? []}
          minLength={3}
          lookup={asyncFetch}
          error={(value?.errors.length ?? 0) > 0}
          preLoadedOptions={preLoadedOptions}
          isOnTemplatePage={isOnTemplatePage}
          templateBuilderDisplayName={templateBuilderDisplayName}
        />
      </Box>
      {['lives or placement in', 'placement in'].includes(operator) && (
        <Tooltip
          title={
            'Manual placements are often temporary and are added where a person’s primary address is outside an area but they need to be visible to users that have access restricted to that location eg. where they have registered to an event or have opted-in to your field campaign. To exclude manual placements, select the “lives in” operator.'
          }
        >
          <FormHelperText>
            Note: Your filter will include contacts with manual placements in the selected
            location(s)
          </FormHelperText>
        </Tooltip>
      )}
    </>
  );
};

export const fetchAutocompleteWidget: BaseWidget &
  MultiSelectFieldSettings<AutocompleteGenericEntity[]> = {
  type: 'select',
  hideOperator: true,
  operatorInlineLabel: 'is in',
  asyncFetch: true,

  useAsyncSearch: true,
  showSearch: true,
  allowCustomValues: true,

  factory: FetchAutocompleteFactory,
};

export const fetchAutoCompleteWithOperatorWidget: BaseWidget &
  MultiSelectFieldSettings<AutocompleteGenericEntity[]> = {
  type: 'select',
  asyncFetch: true,

  useAsyncSearch: true,
  showSearch: true,
  allowCustomValues: true,

  factory: FetchAutocompleteFactory,
};
